import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface SessionData {
  sessionToken?: string;
  projectId?: string;
  currentStep: string;
  workflowData: Record<string, any>;
  lastSaved?: Date;
  isDirty: boolean; // Has unsaved changes
}

interface SessionContextType {
  currentSession: SessionData | null;
  isAutoSaving: boolean;
  lastAutoSave: Date | null;
  sessionError: string | null;
  
  // Session management
  createSession: (projectId: string, step: string, data: Record<string, any>) => Promise<void>;
  updateSession: (step?: string, data?: Record<string, any>) => Promise<void>;
  deactivateSession: () => Promise<void>;
  
  // Auto-save functionality
  markDirty: (data?: Record<string, any>) => void;
  triggerAutoSave: () => Promise<void>;
  
  // Recovery functionality
  checkRecoverySessions: () => Promise<RecoverySession[]>;
  recoverSession: (sessionToken: string) => Promise<void>;
  
  // State management
  clearError: () => void;
  isConnected: boolean;
}

interface RecoverySession {
  sessionToken: string;
  projectName: string;
  lastActivity: string;
  currentStep: string;
  canRecover: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
const AUTO_SAVE_DEBOUNCE = 2000; // 2 seconds debounce

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState<SessionData | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(navigator.onLine);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [autoSaveInterval, setAutoSaveInterval] = useState<NodeJS.Timeout | null>(null);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Set up auto-save interval
  useEffect(() => {
    if (currentSession?.isDirty && currentSession?.sessionToken && isConnected) {
      const interval = setInterval(() => {
        triggerAutoSave();
      }, AUTO_SAVE_INTERVAL);
      
      setAutoSaveInterval(interval);
      
      return () => {
        clearInterval(interval);
        setAutoSaveInterval(null);
      };
    }
  }, [currentSession?.isDirty, currentSession?.sessionToken, isConnected]);

  // Handle page unload - save session state
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentSession?.isDirty) {
        // Save to localStorage as backup
        localStorage.setItem('taylaw_unsaved_session', JSON.stringify({
          ...currentSession,
          timestamp: new Date().toISOString()
        }));
        
        // Show warning
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentSession?.isDirty]);

  // Check for recovery sessions on mount — skip on Desk page since recovery
  // is only relevant for drafting/analysis workflows, and the API call competes
  // with critical Desk data fetches for browser connections.
  useEffect(() => {
    if (user && !window.location.pathname.startsWith('/desk')) {
      checkForRecoverySessions();
    }
  }, [user]);

  const createSession = useCallback(async (projectId: string, step: string, data: Record<string, any>) => {
    try {
      setSessionError(null);
      
      const response = await apiService.createSession({
        projectId,
        currentStep: step,
        sessionData: data
      });
      
      const newSession: SessionData = {
        sessionToken: response.session_token,
        projectId,
        currentStep: step,
        workflowData: data,
        lastSaved: new Date(),
        isDirty: false
      };
      
      setCurrentSession(newSession);
      
      // Clear any old recovery data
      localStorage.removeItem('taylaw_unsaved_session');
      
    } catch (error: any) {
      console.error('Error creating session:', error);
      setSessionError(error.response?.data?.detail || 'Failed to create session');
    }
  }, []);

  const updateSession = useCallback(async (step?: string, data?: Record<string, any>) => {
    if (!currentSession?.sessionToken) return;
    
    try {
      setSessionError(null);
      
      const updateData: Record<string, any> = {};
      if (step) updateData.currentStep = step;
      if (data) updateData.sessionData = { ...currentSession.workflowData, ...data };
      
      await apiService.updateSession(currentSession.sessionToken, updateData);
      
      setCurrentSession(prev => prev ? {
        ...prev,
        currentStep: step || prev.currentStep,
        workflowData: data ? { ...prev.workflowData, ...data } : prev.workflowData,
        lastSaved: new Date(),
        isDirty: false
      } : null);
      
      setLastAutoSave(new Date());
      
    } catch (error: any) {
      console.error('Error updating session:', error);
      setSessionError(error.response?.data?.detail || 'Failed to update session');
    }
  }, [currentSession]);

  const deactivateSession = useCallback(async () => {
    if (!currentSession?.sessionToken) return;
    
    try {
      await apiService.deactivateSession(currentSession.sessionToken);
      setCurrentSession(null);
      setLastAutoSave(null);
      
      // Clear local storage
      localStorage.removeItem('taylaw_unsaved_session');
      
    } catch (error: any) {
      console.error('Error deactivating session:', error);
      // Even if API call fails, clear local session
      setCurrentSession(null);
    }
  }, [currentSession]);

  const markDirty = useCallback((data?: Record<string, any>) => {
    setCurrentSession(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        workflowData: data ? { ...prev.workflowData, ...data } : prev.workflowData,
        isDirty: true
      };
    });
    
    // Debounced auto-save
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      triggerAutoSave();
    }, AUTO_SAVE_DEBOUNCE);
    
    setAutoSaveTimeout(timeout);
  }, [autoSaveTimeout]);

  const triggerAutoSave = useCallback(async () => {
    if (!currentSession?.isDirty || !currentSession?.sessionToken || !isConnected) {
      return;
    }
    
    try {
      setIsAutoSaving(true);
      setSessionError(null);
      
      await apiService.autoSave({
        projectId: currentSession.projectId!,
        currentState: currentSession.workflowData,
        workflowStep: currentSession.currentStep
      });
      
      setCurrentSession(prev => prev ? {
        ...prev,
        lastSaved: new Date(),
        isDirty: false
      } : null);
      
      setLastAutoSave(new Date());
      
    } catch (error: any) {
      console.error('Auto-save failed:', error);
      setSessionError('Auto-save failed. Your work is saved locally.');
      
      // Save to localStorage as backup
      localStorage.setItem('taylaw_unsaved_session', JSON.stringify({
        ...currentSession,
        timestamp: new Date().toISOString()
      }));
      
    } finally {
      setIsAutoSaving(false);
    }
  }, [currentSession, isConnected]);

  const checkRecoverySessions = useCallback(async (): Promise<RecoverySession[]> => {
    if (!user) return [];
    
    try {
      const sessions = await apiService.getRecoverySessions();
      return sessions.map((session: any) => ({
        sessionToken: session.session.session_token,
        projectName: session.project_name,
        lastActivity: session.last_activity,
        currentStep: session.current_step,
        canRecover: session.can_recover
      }));
      
    } catch (error: any) {
      console.error('Error checking recovery sessions:', error);
      return [];
    }
  }, [user]);

  const checkForRecoverySessions = useCallback(async () => {
    const sessions = await checkRecoverySessions();
    
    // Also check localStorage for unsaved work
    const localSession = localStorage.getItem('taylaw_unsaved_session');
    if (localSession) {
      try {
        const parsed = JSON.parse(localSession);
        const timestamp = new Date(parsed.timestamp);
        const now = new Date();
        const hoursSince = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60);
        
        // If less than 24 hours old, consider it recoverable
        if (hoursSince < 24) {
          sessions.push({
            sessionToken: 'local_recovery',
            projectName: 'Unsaved Work (Local)',
            lastActivity: parsed.timestamp,
            currentStep: parsed.currentStep,
            canRecover: true
          });
        }
      } catch (e) {
        // Invalid local session data
        localStorage.removeItem('taylaw_unsaved_session');
      }
    }
    
    if (sessions.length > 0) {
      // Show recovery notification
      console.log('Recovery sessions available:', sessions);
      // This would trigger a UI notification component
    }
  }, [checkRecoverySessions]);

  const recoverSession = useCallback(async (sessionToken: string) => {
    try {
      setSessionError(null);
      
      if (sessionToken === 'local_recovery') {
        // Recover from localStorage
        const localSession = localStorage.getItem('taylaw_unsaved_session');
        if (localSession) {
          const parsed = JSON.parse(localSession);
          setCurrentSession({
            ...parsed,
            lastSaved: new Date(parsed.timestamp),
            isDirty: true // Mark as dirty since it's recovered data
          });
          localStorage.removeItem('taylaw_unsaved_session');
        }
      } else {
        // Recover from server
        const recovery = await apiService.recoverSession(sessionToken);
        
        setCurrentSession({
          sessionToken: recovery.session.session_token,
          projectId: recovery.session.analysis_run_id,
          currentStep: recovery.session.current_step,
          workflowData: recovery.session.session_data,
          lastSaved: new Date(recovery.session.last_activity),
          isDirty: false
        });
      }
      
    } catch (error: any) {
      console.error('Error recovering session:', error);
      setSessionError(error.response?.data?.detail || 'Failed to recover session');
    }
  }, []);

  const clearError = useCallback(() => {
    setSessionError(null);
  }, []);

  const value: SessionContextType = {
    currentSession,
    isAutoSaving,
    lastAutoSave,
    sessionError,
    createSession,
    updateSession,
    deactivateSession,
    markDirty,
    triggerAutoSave,
    checkRecoverySessions,
    recoverSession,
    clearError,
    isConnected
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;